import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from '@emotion/styled';
import { Colors, BrownFontStack } from '@robinpowered/design-system';
import { useLocationsContext } from '../../contexts';
import { useLocalizationContext } from '../../contexts/Localization/LocalizationContext';
import {
  ResourceReservation,
  ResourceReservationConfirmationErrorCode,
  IconType,
} from '../../__generated__/types';
import type { GetCurrentUserReservationsByDateWorkWeekReservations } from '../EventsList/graphql/__generated__/useCustomResourceReservations.generated';
import { SidebarIconByline } from '../InOfficeStatusCardContainer/components/IconByLine';
import { config } from '../../config';
import { getResourceIcon } from './getResourceIcon';

type Props = {
  reservation:
    | GetCurrentUserReservationsByDateWorkWeekReservations
    | ResourceReservation;
  disabled?: boolean;
};

export const CustomResourceCard = ({
  reservation,
  disabled = false,
}: Props): JSX.Element => {
  const { t } = useTranslation('weekSchedule');
  const { timeFormat } = useLocalizationContext();
  const { selectedLocation } = useLocationsContext();

  // Format time range using ISO duration
  const formatTimeRange = (
    start: moment.Moment,
    isoDuration: string
  ): string => {
    const duration = moment.duration(isoDuration);
    const end = start.clone().add(duration);
    return `${start.format(timeFormat)} - ${end.format(timeFormat)}`;
  };

  const getTimeString = (): string => {
    const dateTimeString = `${reservation.date} ${reservation.startTime}`;
    const startMoment = moment(dateTimeString);
    if (!startMoment.isValid()) {
      return 'Invalid date';
    }
    return formatTimeRange(startMoment, reservation.duration as string);
  };

  const resourceDetails = [
    reservation.resource.resourceGroupName
      ? `${reservation.resource.resourceGroupName}`
      : null,
    reservation.resource.floorId
      ? `Floor ${reservation.resource.floorId}`
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  const resourceIconElement =
    getResourceIcon(reservation.resource.icon, Colors.Gray70, 16) || null;

  const checkInWindow = useMemo(() => {
    const resourcePolicy = reservation.resource.policyConfiguration;
    if (resourcePolicy?.advanceCheckInWindow?.duration) {
      return moment
        .duration(resourcePolicy.advanceCheckInWindow.duration)
        .humanize();
    }
    return null;
  }, [reservation.resource.policyConfiguration]);

  const getCheckInRequirements = () => {
    const isConfirmed = reservation.isConfirmed;
    const checkinErrorCodes =
      reservation.confirmationValidationState?.errorCodes || [];

    if (isConfirmed) {
      return {
        icon: 'SUCCESS' as const,
        copy: t('officeDay.visitCard.check_in_requirements.check_in_complete'),
      };
    }

    if (
      checkInWindow &&
      checkinErrorCodes.includes(
        ResourceReservationConfirmationErrorCode.ADVANCED_CONFIRMATION_WINDOW_TOO_EARLY
      )
    ) {
      return {
        icon: 'WARN' as const,
        copy: t(
          'officeDay.visitCard.check_in_requirements.check_in_starts_before_booking',
          {
            time: checkInWindow,
          }
        ),
      };
    }

    return null;
  };

  const checkInRequirements = getCheckInRequirements();

  const handleResourceNameClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!disabled && reservation) {
      const floorId = reservation.resource.floorId;
      const instanceId = reservation.resource.customResourceInstanceId;
      const locationId = selectedLocation?.id;
      const path = `office?selectedLocationId=${locationId}&floorId=${floorId}&customResourceInstanceId=${instanceId}`;
      window.location.href = `${config.dashboardUrl}/${path}`;
    }
  };

  const ResourceIconByline = ({
    resourceIcon,
    resourceName,
    disabled,
  }: {
    resourceIcon: JSX.Element | null;
    resourceName: string;
    disabled?: boolean;
  }): JSX.Element => (
    <ResourceIconBylineContainer>
      <ResourceIconContainer>{resourceIcon}</ResourceIconContainer>
      {disabled ? (
        <ResourceNameText>{resourceName}</ResourceNameText>
      ) : (
        <ResourceNameLink href="#" onClick={handleResourceNameClick}>
          {resourceName}
        </ResourceNameLink>
      )}
    </ResourceIconBylineContainer>
  );

  return (
    <CustomResourceCardContainer>
      {reservation.isAssigned && (
        <AssignedTagline>{t('assigned')}</AssignedTagline>
      )}
      <Top>
        <Container disabled={disabled}>
          {getTimeString() && <TimeInfo>{getTimeString()}</TimeInfo>}
          <ResourceIconByline
            resourceIcon={resourceIconElement}
            resourceName={reservation.resource.name}
            disabled={disabled}
          />
          {resourceDetails && (
            <ResourceDetailsBottom>{resourceDetails}</ResourceDetailsBottom>
          )}
        </Container>

        {/* Ellipsis dropdown removed per request */}
      </Top>

      {checkInRequirements && (
        <CheckInRequirementsContainer>
          <SidebarIconByline
            type={checkInRequirements.icon}
            copy={checkInRequirements.copy}
          />
        </CheckInRequirementsContainer>
      )}
    </CustomResourceCardContainer>
  );
};

const CustomResourceCardContainer = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Top = styled.div`
  display: flex;
  width: 100%;
`;

const Container = styled.div<{ disabled: boolean }>`
  display: flex;
  flex-direction: column;
  width: 90%;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const TimeInfo = styled.div`
  padding-bottom: 8px;
  color: ${Colors.Gray80};
`;

const ResourceIconBylineContainer = styled.div`
  width: 100%;
  display: flex;
`;

const ResourceIconContainer = styled.div`
  margin-top: 2px;
`;

const ResourceNameLink = styled.a`
  font-family: ${BrownFontStack};
  cursor: pointer;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${Colors.Gray100};
  border-radius: 4px;
  border: none;
  margin-left: 4px;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: ${Colors.Gray30};
  &:hover {
    color: ${Colors.Blue100};
    text-decoration-color: ${Colors.Blue100};
  }
  &:focus {
    outline: none;
    text-decoration: none;
    box-shadow: 0 0 0 4px ${Colors.Blue100};
  }
`;

const ResourceNameText = styled.div`
  margin-left: 4px;
  font-family: ${BrownFontStack};
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${Colors.Gray100};
`;

const ResourceDetailsBottom = styled.div`
  margin-left: 20px;
  margin-top: 8px;
  width: calc(100% - 20px);
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.Gray80};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const CheckInRequirementsContainer = styled.div`
  width: 100%;
  margin-top: 4px;
`;

const AssignedTagline = styled.div`
  width: 100%;
  margin-bottom: 8px;
  border-radius: 4px;
  font-family: ${BrownFontStack};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: ${Colors.Gray80};
  text-align: left;
`;
